import React, {
  Dispatch, Ref, SetStateAction, useEffect, useState,
} from 'react';
import {
  StyleSheet, View, Text, ScrollView,
} from 'react-native';
import Modal from 'react-native-modal';
import AsyncStorage from '@react-native-async-storage/async-storage';
import { useSafeAreaInsets } from 'react-native-safe-area-context';
import isMobile from '../../../components/IsMobile';
import { logEvent } from '../../../components/LogEvent';
import SelectButtonTile from './SelectButtonTile';
import Colors from '../../../constants/Colors';
import Button from '../../../components/Button';
import Style from '../../../constants/Style';
import { Template } from '../../../constants/Types';

interface SelectTemplatesModalProps {
  isVisible: boolean,
  hide: () => void,
  scrollOffset: number,
  scrollTo,
  onScroll,
  scrollViewRef: Ref<ScrollView>,
  templates: Template[],
  selectedTemplateIds: string[],
  setSelectedTemplateIds: Dispatch<SetStateAction<string[]>>,
}

const SelectTemplatesModal = ({
  isVisible, hide, scrollOffset, scrollTo, scrollViewRef, onScroll, templates, selectedTemplateIds,
  setSelectedTemplateIds,
}: SelectTemplatesModalProps) => {
  const insets = useSafeAreaInsets();
  const [currentSelection, setCurrentSelection] = useState<string[]>(selectedTemplateIds ?? []);

  useEffect(() => setCurrentSelection(selectedTemplateIds), [selectedTemplateIds]);

  const save: () => void = () => {
    setSelectedTemplateIds(currentSelection);
    AsyncStorage.setItem('solveAnalyticsSelectedTemplates', JSON.stringify(currentSelection));
    hide();
    logEvent('SolveAnalytics', 'Save_button_selection');
  };

  const cancel: () => void = () => {
    hide();
    setCurrentSelection(selectedTemplateIds);
    logEvent('SolveAnalytics', 'Cancel_button_selection');
  };

  return <Modal
    animationIn={isMobile ? 'slideInUp' : 'fadeIn'}
    animationOut={isMobile ? 'slideOutDown' : 'fadeOut'}
    {...{ isVisible }}
    swipeDirection={['down']}
    onSwipeComplete={hide}
    onBackButtonPress={() => {
      logEvent('SolveAnalytics', 'modal_back');
      hide();
    }}
    onBackdropPress={hide}
    propagateSwipe
    {...{ scrollOffset, scrollTo }}
    scrollOffsetMax={200}
  >
    <View
      style={[Style.modalContent, { flex: 1, marginTop: insets.top, marginBottom: insets.bottom }]}
    >
      <Text style={styles.headerText}>{'Choose templates to add'}</Text>
      <ScrollView
        scrollEventThrottle={1600}
        {...{ onScroll }}
        ref={scrollViewRef}
      >
        <View style={styles.grid}>
          {templates?.map((item: Template, index: number) => {
            const selected: boolean = currentSelection.includes(item.id);

            return <SelectButtonTile
              {...{ selected }}
              title={item.name}
              onSelect={() => {
                if (selected) setCurrentSelection(currentSelection.filter((id) => id !== item.id));
                else setCurrentSelection([...currentSelection, item.id]);
              }}
              key={`template_tile_${item.name}`}
              testID={`template-card#${index + 1}`}
            />;
          })}
        </View>
      </ScrollView>
      <View style={styles.buttonContainer}>
        <Button
          affirmative
          title='Save'
          onPress={save}
          testID='save-template-cards-btn'
          style={{ marginStart: 24 }}
        />
        <Button
          title='Cancel'
          onPress={cancel}
          testID='close-template-cards-btn'
          style={styles.cancelButton}
        />
      </View>
    </View>
  </Modal>;
};

const styles = StyleSheet.create({
  headerText: {
    fontSize: 20,
    lineHeight: 30,
    fontFamily: 'Poppins_400Regular',
    color: Colors.light.hereworksPrimaryGrey,
    marginVertical: 24,
    alignSelf: 'center',
    textAlign: 'center',
  },
  grid: {
    flexDirection: 'row',
    flexWrap: 'wrap',
    justifyContent: 'center',
  },
  buttonContainer: {
    flexDirection: 'row',
    alignSelf: 'center',
    marginVertical: 24,
  },
  cancelButton: {
    marginStart: 16,
    marginEnd: 24,
  },
});

export default SelectTemplatesModal;
